import { useState } from 'react';

interface BookingCardProps {
  checkInDate: string;
  checkOutDate: string;
  guests: number;
  rooms: number;
  onCheckInChange: (date: string) => void;
  onCheckOutChange: (date: string) => void;
  onGuestsChange: (guests: number) => void;
  onRoomsChange: (rooms: number) => void;
}

export default function BookingCard({
  checkInDate,
  checkOutDate,
  guests,
  rooms,
  onCheckInChange,
  onCheckOutChange,
  onGuestsChange,
  onRoomsChange
}: BookingCardProps) {
  const [showGuestPicker, setShowGuestPicker] = useState(false);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
      <div className="mb-6">
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-3xl font-bold text-gray-900">₩89,000</span>
          <span className="text-sm text-gray-500">/ 박</span>
        </div>
        <p className="text-sm text-gray-600">세금 및 수수료 포함</p>
      </div>

      <div className="space-y-4 mb-6">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">체크인</label>
            <input
              type="date"
              value={checkInDate}
              onChange={(e) => onCheckInChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">체크아웃</label>
            <input
              type="date"
              value={checkOutDate}
              onChange={(e) => onCheckOutChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
            />
          </div>
        </div>

        <div className="relative">
          <label className="block text-xs font-medium text-gray-700 mb-1">투숙객 및 객실</label>
          <button
            onClick={() => setShowGuestPicker(!showGuestPicker)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-left flex items-center justify-between hover:border-gray-400 transition-colors cursor-pointer"
          >
            <span>투숙객 {guests}명, 객실 {rooms}개</span>
            <i className="ri-arrow-down-s-line"></i>
          </button>

          {showGuestPicker && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-10">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">투숙객</span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => onGuestsChange(Math.max(1, guests - 1))}
                      className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-full hover:border-teal-500 hover:text-teal-500 transition-colors cursor-pointer"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="w-8 text-center text-sm font-medium">{guests}</span>
                    <button
                      onClick={() => onGuestsChange(guests + 1)}
                      className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-full hover:border-teal-500 hover:text-teal-500 transition-colors cursor-pointer"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">객실</span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => onRoomsChange(Math.max(1, rooms - 1))}
                      className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-full hover:border-teal-500 hover:text-teal-500 transition-colors cursor-pointer"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="w-8 text-center text-sm font-medium">{rooms}</span>
                    <button
                      onClick={() => onRoomsChange(rooms + 1)}
                      className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-full hover:border-teal-500 hover:text-teal-500 transition-colors cursor-pointer"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowGuestPicker(false)}
                className="w-full mt-4 bg-teal-600 text-white py-2 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                완료
              </button>
            </div>
          )}
        </div>
      </div>

      <button className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors mb-3 whitespace-nowrap cursor-pointer">
        예약하기
      </button>

      <p className="text-xs text-center text-gray-500">지금 예약하고 나중에 결제하세요</p>

      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600">₩89,000 × 1박</span>
          <span className="text-sm font-medium text-gray-900">₩89,000</span>
        </div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600">세금 및 수수료</span>
          <span className="text-sm font-medium text-gray-900">₩8,900</span>
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-gray-200">
          <span className="text-base font-bold text-gray-900">총 금액</span>
          <span className="text-base font-bold text-gray-900">₩97,900</span>
        </div>
      </div>
    </div>
  );
}